/**
 * Datenabrufe als React-Query-Hooks.
 *
 * Die teuren Ideen-Listen werden lange frisch gehalten (30 Min.) und nur bei
 * Netzfehlern einmal wiederholt -- nie nach einer Zeitueberschreitung, s.
 * `ohneZeitueberschreitung`.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../contexts/AuthContext'
import { ApiError } from '../lib/api'
import { QualyzApi, type FilterRequest, type UnterbewertetAnfrage, type Watchlist } from '../services/qualyz'

const MINUTE = 60_000

/** Ein 4xx wird nicht besser, wenn man ihn wiederholt -- ein Netzfehler vielleicht. */
function einmalBeiNetzfehler(versuche: number, fehler: unknown): boolean {
  if (fehler instanceof ApiError && fehler.status >= 400 && fehler.status < 500) return false
  return versuche < 1
}

/**
 * Nach einer Zeitueberschreitung (504) nicht wiederholen (14.09.2026). Der
 * Proxy bricht nach 60 Sekunden ab, das Backend rechnet aber weiter -- eine
 * Wiederholung startete dieselbe Rechnung ein zweites Mal daneben. So stauten
 * sich in der Nacht zum 14.09. die Anfragen, bis jede ueber 60 Sekunden brauchte.
 */
function ohneZeitueberschreitung(versuche: number, fehler: unknown): boolean {
  if (fehler instanceof ApiError && fehler.status === 504) return false
  return einmalBeiNetzfehler(versuche, fehler)
}

export function useRegime() {
  return useQuery({
    queryKey: ['regime'],
    queryFn: QualyzApi.regime,
    staleTime: 10 * MINUTE,
    retry: einmalBeiNetzfehler,
  })
}

export function useHealth() {
  return useQuery({
    queryKey: ['health'],
    queryFn: QualyzApi.health,
    staleTime: 5 * MINUTE,
    retry: einmalBeiNetzfehler,
  })
}

/** Bestandszahlen und Datenstand fuer Start und Status. */
export function useStatus() {
  return useQuery({
    queryKey: ['status'],
    queryFn: QualyzApi.status,
    staleTime: 10 * MINUTE,
    retry: false,
  })
}

export function useApiInfo() {
  return useQuery({
    queryKey: ['api-info'],
    queryFn: QualyzApi.info,
    staleTime: 5 * MINUTE,
    retry: einmalBeiNetzfehler,
  })
}

export function useTopIdeas(limit = 20) {
  const { isAuthenticated } = useAuth()
  return useQuery({
    queryKey: ['top-ideas', limit],
    queryFn: () => QualyzApi.topIdeas(limit),
    enabled: isAuthenticated,
    staleTime: 30 * MINUTE,
    retry: ohneZeitueberschreitung,
  })
}

/**
 * Der oeffentliche Ausschnitt fuer Besucher. Kommt immer sofort aus dem
 * Zwischenspeicher; rechnet das Backend gerade neu, fragt die Seite alle
 * 30 Sekunden nach, bis die Liste da ist.
 */
export function useTeaser() {
  const { isAuthenticated, isLoading } = useAuth()
  return useQuery({
    queryKey: ['teaser'],
    queryFn: QualyzApi.teaser,
    enabled: !isLoading && !isAuthenticated,
    staleTime: 10 * MINUTE,
    refetchInterval: (query) => (query.state.data?.wird_berechnet ? 30_000 : false),
    retry: einmalBeiNetzfehler,
  })
}

/**
 * Einzelansicht je Aktie. Die Stufe steht im Schluessel: Nach dem Anmelden
 * zeigt dieselbe Seite mehr, ohne alte Daten aus dem Speicher.
 */
export function useAktie(symbol: string | undefined) {
  const { user, isLoading } = useAuth()
  const stufe = user ? (user.is_pro ? 'pro' : 'kostenlos') : 'besucher'
  return useQuery({
    queryKey: ['aktie', (symbol ?? '').toUpperCase(), stufe],
    queryFn: () => QualyzApi.aktie(symbol as string),
    enabled: !!symbol && !isLoading,
    staleTime: 10 * MINUTE,
    retry: einmalBeiNetzfehler,
  })
}

export function useLinsenRegeln() {
  return useQuery({
    queryKey: ['linsen-regeln'],
    queryFn: QualyzApi.linsenRegeln,
    staleTime: 60 * MINUTE,
    retry: einmalBeiNetzfehler,
  })
}

/** Suche im Kopf -- die Eingabe entprellt die Komponente selbst. */
export function useSuche(begriff: string) {
  const eingabe = begriff.trim()
  return useQuery({
    queryKey: ['suche', eingabe.toUpperCase()],
    queryFn: () => QualyzApi.suche(eingabe),
    enabled: eingabe.length > 0,
    staleTime: 5 * MINUTE,
    retry: false,
    placeholderData: keepPreviousData,
  })
}

export function useIdeasFilter(req: FilterRequest) {
  const { isAuthenticated } = useAuth()
  return useQuery({
    queryKey: ['ideas-filter', req.lenses?.join(',') ?? 'alle', req.gates?.join(',') ?? 'keine', req.limit],
    queryFn: () => QualyzApi.filterIdeas(req),
    enabled: isAuthenticated,
    staleTime: 30 * MINUTE,
    placeholderData: keepPreviousData,
    retry: ohneZeitueberschreitung,
  })
}

/**
 * Unterbewertete Aktien -- vorgewaermt, jede Auswahl kostet nur das Filtern.
 * Liegt die Liste noch nicht vor (`wird_berechnet`), alle 30 Sekunden nachsehen.
 */
export function useUnterbewertet(a: UnterbewertetAnfrage) {
  const { isAuthenticated } = useAuth()
  return useQuery({
    queryKey: ['unterbewertet', a.basen.join(','), a.alle, a.min_potenzial, a.min_quality],
    queryFn: () => QualyzApi.unterbewertet(a),
    enabled: isAuthenticated,
    staleTime: 30 * MINUTE,
    placeholderData: keepPreviousData,
    refetchInterval: (query) => (query.state.data?.wird_berechnet ? 30_000 : false),
    retry: einmalBeiNetzfehler,
  })
}

export function useIdeasOptions() {
  return useQuery({
    queryKey: ['ideas-options'],
    queryFn: QualyzApi.ideasOptions,
    staleTime: 60 * MINUTE,
    retry: einmalBeiNetzfehler,
  })
}

export function useWatchlist() {
  const { isAuthenticated } = useAuth()
  return useQuery({
    queryKey: ['watchlist'],
    queryFn: QualyzApi.watchlist,
    enabled: isAuthenticated,
    staleTime: 5 * MINUTE,
    retry: einmalBeiNetzfehler,
  })
}

export function useWatchlistToggle() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ symbol, merken }: { symbol: string; merken: boolean }) =>
      merken ? QualyzApi.watchlistAdd(symbol) : QualyzApi.watchlistRemove(symbol),
    onSuccess: (liste: Watchlist) => {
      client.setQueryData(['watchlist'], liste)
    },
  })
}

export function useJobs() {
  const { isAdmin } = useAuth()
  return useQuery({
    queryKey: ['admin-jobs'],
    queryFn: QualyzApi.jobs,
    enabled: isAdmin,
    staleTime: MINUTE,
    retry: einmalBeiNetzfehler,
  })
}

export function useTriggerJob() {
  return useMutation({
    mutationFn: (schluessel: string) => QualyzApi.triggerJob(schluessel),
  })
}

export function useProAnfragen(status?: string) {
  const { isAdmin } = useAuth()
  return useQuery({
    queryKey: ['pro-anfragen', status ?? 'alle'],
    queryFn: () => QualyzApi.proAnfragen(status),
    enabled: isAdmin,
    staleTime: MINUTE,
    retry: einmalBeiNetzfehler,
  })
}

export function useProAnfrageEntscheiden() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, freischalten }: { id: string; freischalten: boolean }) =>
      QualyzApi.proAnfrageEntscheiden(id, freischalten),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pro-anfragen'] }),
  })
}

export function useEigeneProAnfrage() {
  const { isAuthenticated } = useAuth()
  return useQuery({
    queryKey: ['pro-anfrage-eigene'],
    queryFn: QualyzApi.proAnfrageEigene,
    enabled: isAuthenticated,
    staleTime: MINUTE,
    retry: einmalBeiNetzfehler,
  })
}

export function useProAnfrageStellen() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (nachricht?: string) => QualyzApi.proAnfrageStellen(nachricht),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pro-anfrage-eigene'] }),
  })
}

export function useDatenwaechter() {
  const { isAdmin } = useAuth()
  return useQuery({
    queryKey: ['datenwaechter'],
    queryFn: QualyzApi.datenwaechter,
    enabled: isAdmin,
    staleTime: MINUTE,
    retry: einmalBeiNetzfehler,
  })
}

export function useDatenwaechterFreigeben() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: QualyzApi.datenwaechterFreigeben,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['datenwaechter'] }),
  })
}
